(ns ui.data-grid.condition-value-filter
  (:require
   ["ag-grid-react" :refer [useGridFilter]]
   ["virtua" :refer [Virtualizer]]
   [applied-science.js-interop :as j]
   [clojure.string :as str]
   [react :as react]
   [reagent.core :as reagent]
   [ui.button :refer [button]]
   [ui.fields :as fields]))

(def ^:private blank-key "blank:")
(def ^:private blank-label "(Blank cells)")

(def ^:private text-condition-options
  [{:value ""            :label "None"}
   {:value "contains"    :label "Contains"}
   {:value "notContains" :label "Does not contain"}
   {:value "equal"       :label "Equals"}
   {:value "notEqual"    :label "Does not equal"}
   {:value "beginsWith"  :label "Begins with"}
   {:value "endsWith"    :label "Ends with"}
   {:value "empty"       :label "Is empty"}
   {:value "notEmpty"    :label "Is not empty"}])

(def ^:private numeric-condition-options
  [{:value ""                   :label "None"}
   {:value "equal"              :label "Equals"}
   {:value "notEqual"           :label "Does not equal"}
   {:value "greaterThan"        :label "Greater than"}
   {:value "greaterThanOrEqual" :label "Greater than or equal"}
   {:value "lessThan"           :label "Less than"}
   {:value "lessThanOrEqual"    :label "Less than or equal"}
   {:value "empty"              :label "Is empty"}
   {:value "notEmpty"           :label "Is not empty"}])

(def ^:private no-value-conditions
  #{"" "empty" "notEmpty"})

(defn- blank-value? [value]
  (or (nil? value)
      (and (string? value)
           (str/blank? value))))

(defn- parse-number [value]
  (when-not (blank-value? value)
    (let [parsed (js/Number value)]
      (when-not (js/isNaN parsed)
        parsed))))

(defn- value-type-tag [value]
  (cond
    (number? value)  "n"
    (boolean? value) "b"
    :else            "s"))

(defn- value-key [value]
  (if (blank-value? value)
    blank-key
    (str "value:" (value-type-tag value) ":" value)))

(defn- value-label [value]
  (if (blank-value? value)
    blank-label
    (str value)))

(defn collect-distinct-values [params]
  (let [items (volatile! {})]
    (j/call (j/get params :api) :forEachNode
            (fn [node]
              (let [value (j/call params :getValue node)
                    key   (value-key value)]
                (vswap! items assoc key {:key key :label (value-label value) :value value}))))
    (let [items-by-key @items
          items        (vals items-by-key)
          nonblank     (remove #(= blank-key (:key %)) items)
          numeric?     (and (seq nonblank)
                            (every? #(some? (parse-number (:value %))) nonblank))
          sorted       (sort-by (if numeric?
                                   #(parse-number (:value %))
                                   #(str/lower-case (:label %)))
                                 nonblank)
          blank        (get items-by-key blank-key)]
      {:items    (cond-> (vec sorted) blank (conj blank))
       :numeric? numeric?})))

(defn- default-condition []
  {:type "" :value ""})

(defn- default-draft [all-keys]
  {:condition1     (default-condition)
   :condition2     (default-condition)
   :operation      "and"
   :selectedValues all-keys})

(defn- model->draft [model all-keys]
  (if model
    {:condition1     (merge (default-condition) (or (:condition1 model) (:condition model)))
     :condition2     (merge (default-condition) (:condition2 model))
     :operation      (or (:operation model) "and")
     :selectedValues (if (some? (:selectedValues model))
                       (set (:selectedValues model))
                       all-keys)}
    (default-draft all-keys)))

(defn- condition-active? [condition]
  (not (str/blank? (:type condition))))

(defn- value-filter-active? [selected-values all-keys]
  (not= selected-values all-keys))

(defn- draft->model [draft all-keys]
  (let [condition1 (:condition1 draft)
        condition2 (:condition2 draft)]
    (when (or (condition-active? condition1)
              (condition-active? condition2)
              (value-filter-active? (:selectedValues draft) all-keys))
      {:filterType     "conditionValue"
       :condition1     condition1
       :condition2     (when (condition-active? condition2) condition2)
       :operation      (when (condition-active? condition2) (:operation draft))
       :selectedValues (when (value-filter-active? (:selectedValues draft) all-keys)
                         (->> (:selectedValues draft) sort vec))})))

(defn- condition-passes?
  [{:keys [type value]} raw]
  (let [raw-text   (str/lower-case (str (or raw "")))
        query-text (str/lower-case (str (or value "")))
        raw-number (parse-number raw)
        query-num  (parse-number value)]
    (case type
      "" true
      "contains"           (str/includes? raw-text query-text)
      "notContains"        (not (str/includes? raw-text query-text))
      "equal"              (= raw-text query-text)
      "notEqual"           (not= raw-text query-text)
      "beginsWith"         (str/starts-with? raw-text query-text)
      "endsWith"           (str/ends-with? raw-text query-text)
      "empty"              (blank-value? raw)
      "notEmpty"           (not (blank-value? raw))
      "greaterThan"        (and (some? raw-number) (some? query-num) (> raw-number query-num))
      "greaterThanOrEqual" (and (some? raw-number) (some? query-num) (>= raw-number query-num))
      "lessThan"           (and (some? raw-number) (some? query-num) (< raw-number query-num))
      "lessThanOrEqual"    (and (some? raw-number) (some? query-num) (<= raw-number query-num))
      true)))

(defn- conditions-pass? [model raw]
  (let [condition1     (:condition1 model)
        condition2     (:condition2 model)
        first-active?  (condition-active? condition1)
        second-active? (condition-active? condition2)
        first-pass?    (if first-active? (condition-passes? condition1 raw) true)
        second-pass?   (if second-active? (condition-passes? condition2 raw) true)]
    (cond
      (and first-active? second-active?)
      (case (:operation model)
        "or"  (or first-pass? second-pass?)
        "and" (and first-pass? second-pass?)
        (and first-pass? second-pass?))

      first-active?  first-pass?
      second-active? second-pass?
      :else          true)))

(defn- condition-select [{:keys [condition-options condition on-change]}]
  [fields/select
   {:native    true
    :size      :small
    :value     (:type condition)
    :on-change #(on-change (j/get-in % [:target :value]))
    :options   (mapv (fn [{:keys [value label]}] {:value value :text label}) condition-options)}])

(defn- condition-value-input [{:keys [condition on-change]}]
  (when-not (contains? no-value-conditions (:type condition))
    [fields/input
     {:class       "min-w-0!"
      :value       (:value condition)
      :placeholder "Value"
      :on-change   #(on-change (j/get-in % [:target :value]))}]))

(defn- viewport-height []
  (or (some-> js/window .-visualViewport .-height)
      (some-> js/document .-documentElement .-clientHeight)
      (.-innerHeight js/window)))

(defn- viewport-max-height []
  (str (js/Math.floor (viewport-height)) "px"))

(defn- ag-popup-parent [root]
  (some-> root (.closest ".ag-menu") (.closest ".ag-popup") .-parentElement))

(defn- popup-max-height [root]
  (when-let [menu (.closest root ".ag-menu")]
    (when-let [popup-parent (ag-popup-parent root)]
      (let [viewport-bottom (viewport-height)
            parent-rect     (.getBoundingClientRect popup-parent)
            visible-top     (max 0 (.-top parent-rect))
            visible-bottom  (min viewport-bottom (.-bottom parent-rect))
            menu-chrome     (max 0 (- (.-offsetHeight menu) (.-clientHeight menu)))
            available       (js/Math.floor (- visible-bottom visible-top menu-chrome))]
        (when (pos? available)
          (str available "px"))))))

(defn condition-value-filter [props]
  (let [[distinct-values set-distinct-values] (react/useState #(collect-distinct-values props))
        {:keys [items numeric?]} distinct-values
        all-keys                              (set (map :key items))
        [draft set-draft]                     (react/useState #(default-draft all-keys))
        [search set-search]                   (react/useState "")
        hide-popup-ref                        (react/useRef nil)
        root-ref                              (react/useRef nil)
        boundary-observer-ref                 (react/useRef nil)
        checklist-ref                         (react/useRef nil)
        virtualizer-ref                       (react/useRef nil)
        [max-popup-height set-max-popup-height] (react/useState viewport-max-height)
        applied-model    (some-> (j/get props :model) (js->clj :keywordize-keys true))
        selected-values  (some-> (:selectedValues applied-model) set)
        on-model-change  (j/get props :onModelChange)

        close-popup!
        #(when-let [hide-popup (j/get hide-popup-ref :current)]
           (hide-popup))

        reset-draft!
        (fn []
          (set-search "")
          (set-draft (model->draft applied-model all-keys)))

        refresh-popup-height!
        (fn []
          (when-let [root (j/get root-ref :current)]
            (set-max-popup-height (or (popup-max-height root) (viewport-max-height)))))

        disconnect-boundary-observer!
        (fn []
          (when-let [observer (j/get boundary-observer-ref :current)]
            (.disconnect observer)
            (j/assoc! boundary-observer-ref :current nil)))

        does-filter-pass
        (fn [params]
          (if-not applied-model
            true
            (let [raw (j/call props :getValue (j/get params :node))]
              (and (conditions-pass? applied-model raw)
                   (or (nil? selected-values)
                       (contains? selected-values (value-key raw)))))))

        after-gui-attached
        (fn [params]
          (let [fresh-distinct-values (collect-distinct-values props)
                fresh-all-keys        (set (map :key (:items fresh-distinct-values)))]
            (j/assoc! hide-popup-ref :current (j/get params :hidePopup))
            (set-distinct-values fresh-distinct-values)
            (set-search "")
            (set-draft (model->draft applied-model fresh-all-keys))
            (when-let [root (j/get root-ref :current)]
              (disconnect-boundary-observer!)
              (refresh-popup-height!)
              (when-let [popup-parent (ag-popup-parent root)]
                (let [observer (js/ResizeObserver. (fn [] (refresh-popup-height!)))]
                  (.observe observer popup-parent)
                  (j/assoc! boundary-observer-ref :current observer))))))

        after-gui-detached
        (fn []
          (disconnect-boundary-observer!)
          (j/assoc! hide-popup-ref :current nil))]

    (useGridFilter
     #js {:doesFilterPass   does-filter-pass
          :afterGuiAttached after-gui-attached
          :afterGuiDetached after-gui-detached})

    (let [condition-options (if numeric? numeric-condition-options text-condition-options)

          visible-items
          (if (str/blank? search)
            items
            (filterv #(str/includes? (str/lower-case (:label %)) (str/lower-case search)) items))

          condition1               (:condition1 draft)
          condition2               (:condition2 draft)
          first-condition-active?  (condition-active? condition1)

          set-condition1-type!
          (fn [value]
            (set-draft
             (fn [current]
               (if (str/blank? value)
                 (-> current
                     (assoc :condition1 (default-condition))
                     (assoc :condition2 (default-condition))
                     (assoc :operation "and"))
                 (-> current
                     (assoc-in [:condition1 :type] value)
                     (assoc-in [:condition1 :value] ""))))))

          set-condition2-type!
          (fn [value]
            (set-draft
             (fn [current]
               (assoc current :condition2
                      (if (str/blank? value)
                        (default-condition)
                        {:type value :value ""})))))

          render-value
          (fn [idx]
            (let [{:keys [key label]} (nth visible-items idx)]
              (reagent/as-element
               (with-meta
                [fields/checkbox
                 {:class "h-4 w-4"
                  :classes
                  {:root
                   "min-w-max items-center gap-1.5 py-0.5 text-xs leading-4 [&>label]:whitespace-nowrap"}
                  :label label
                  :checked (contains? (:selectedValues draft) key)
                  :on-change
                  (fn [checked?]
                    (set-draft
                     (fn [current]
                       (update current :selectedValues
                               (fn [selected]
                                 ((if checked? conj disj) selected key))))))}]
                {:key key}))))

          apply!
          (fn []
            (on-model-change (some-> (draft->model draft all-keys) clj->js))
            (close-popup!))

          cancel!
          (fn []
            (reset-draft!)
            (close-popup!))]

      (react/useEffect
       (fn []
         (when-let [virtualizer (j/get virtualizer-ref :current)]
           (j/call virtualizer :scrollTo 0))
         js/undefined)
       #js [search distinct-values])

      (reagent/as-element
       [:div
        {:class "flex min-h-0 w-[280px] flex-col overflow-hidden bg-kleene-background text-xs text-kleene-text"
         :ref   root-ref
         :style {:max-height max-popup-height}
         :data-ag-condition-value-filter ""
         :on-key-down
         (fn [event]
           (.stopPropagation event)
           (when (= "Escape" (.-key event))
             (.preventDefault event)
             (cancel!)))}

        [:div
         {:class "flex shrink-0 flex-col px-3 pt-3 pb-2"}

         [:span {:class "mb-2 text-xs font-normal leading-4 text-kleene-text"}
          "Filter by condition:"]

         [:div {:class "flex flex-col gap-2"}
          [condition-select
           {:condition-options condition-options
            :condition         condition1
            :on-change         set-condition1-type!}]

          [condition-value-input
           {:condition condition1
            :on-change #(set-draft (fn [current] (assoc-in current [:condition1 :value] %)))}]]

         (when first-condition-active?
           [:div
            {:class "mt-2 flex flex-col gap-2"}

            [condition-select
             {:condition-options condition-options
              :condition         condition2
              :on-change         set-condition2-type!}]

            [condition-value-input
             {:condition condition2
              :on-change #(set-draft (fn [current] (assoc-in current [:condition2 :value] %)))}]

            [fields/radio-root
             {:id              "ag-condition-operation"
              :class           "flex items-center gap-5 pt-1"
              :value           (:operation draft)
              :on-value-change #(set-draft (fn [current] (assoc current :operation %)))}
             (for [{:keys [value label]}
                   [{:value "and" :label "And"}
                    {:value "or" :label "Or"}]]
               ^{:key value}
               [:div {:class "flex items-center gap-1.5"}
                [fields/radio-item {:id "ag-condition-operation" :value value}]
                [:label
                 {:class   "cursor-pointer"
                  :htmlFor (str "ag-condition-operation-" value)}
                 label]])]])]

        [:div
         {:class "flex shrink-0 flex-col gap-2 px-3 pt-2 pb-0"}

         [:span {:class "text-xs font-normal leading-4 text-kleene-text"}
          "Filter by value:"]

         [fields/input
          {:class       "min-w-0!"
           :value       search
           :placeholder "Search"
           :input-props {:on-key-down
                          (fn [event]
                            (when (= "Enter" (.-key event))
                              (.preventDefault event)
                              (apply!)))}
           :on-change   #(set-search (j/get-in % [:target :value]))}]

         [:div
          {:class "flex items-center gap-3 py-1.5"}

          [button
           {:variant  :empty
            :size     :micro
            :on-click #(set-draft (fn [current] (assoc current :selectedValues all-keys)))}
           "Select all"]

          [button
           {:variant  :empty
            :size     :micro
            :on-click #(set-draft (fn [current] (assoc current :selectedValues #{})))}
           "Clear"]]]

        [:div
         {:class "mx-3 mt-1 flex min-h-0 max-h-[150px] flex-1 flex-col overflow-x-auto overflow-y-auto border-t border-kleene-layout-border py-1.5"
          :ref   checklist-ref}

         [:div
          {:class "min-h-16 min-w-max"}

          (when (seq visible-items)
            [:> Virtualizer
             {:ref        virtualizer-ref
              :scroll-ref checklist-ref
              :count      (count visible-items)
              :item-size  20
              :overscan   2}
             render-value])

          (when (empty? visible-items)
            [:span {:class "block py-2 text-xs text-kleene-text-secondary"}
             "No matching values"])]]

        [:div
         {:class "mt-1 flex shrink-0 items-center gap-2 border-t border-kleene-layout-border p-3"}

         [button
          {:size     :small
           :class    "h-8 flex-1 rounded-[4px]! px-3! py-1! text-xs [&>div]:justify-center! [&>div>div]:flex-none! [&>div>div]:text-center!"
           :on-click apply!}
          "OK"]

         [button
          {:variant  :secondary
           :size     :small
           :class    "h-8 flex-1 rounded-[4px]! px-3! py-1! text-xs [&>div]:justify-center! [&>div>div]:flex-none! [&>div>div]:text-center!"
           :on-click cancel!}
          "Cancel"]]]))))
